import { DOCUMENT } from '@angular/common';
import {
  AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, signal, viewChild
} from '@angular/core';
import { ButtonDirective } from '@coreui/angular-pro';
import { CONSENT_APP_CONFIG, ConsentCategory, ConsentService, ConsentSurface, TrackingService } from 'analytics';
import { ENVIRONMENT } from 'shared';
import { ConsentTextsService } from './consent-texts.service';

/**
 * Cookie banner + preference center of the WolfPack Tracking Engine V2
 * (docs/tracking/EVENT_CONTRACT_V2.md §1). Generic for every brand app.
 *
 * Not placed in any template: `provideCookieConsent()` mounts ONE instance on
 * `document.body` once the root component is bootstrapped, so it covers every
 * entry of the app — the default layout as well as login, the direct checkout
 * and the magic-link pages, which live outside the layout.
 *
 * - Banner (bottom sheet, non-modal — no cookie wall: the page stays usable):
 *   three equally weighted buttons "Accept all", "Necessary only", "Settings".
 * - Preference center (modal dialog): necessary locked on, analytics, marketing
 *   (only selectable with analytics), "Save selection".
 * - Texts are shown only in a language the brand lists in `banner_languages`
 *   (else the master language, ConsentTextsService); the overlay stays hidden
 *   until the config and those translations are there.
 * - While no "Cookie settings" link is on screen (the layout's sidebar footer
 *   registers one), a small floating link keeps the preference center reachable.
 */
@Component({
  selector: 'app-cookie-consent-overlay',
  standalone: true,
  imports: [ButtonDirective],
  template: `
    @if (showBanner()) {
      <section class="wpc-banner" role="dialog" [attr.lang]="texts.lang()" [attr.dir]="texts.dir()" aria-modal="false"
               aria-labelledby="wpc-banner-title" aria-describedby="wpc-banner-text">
        <div class="wpc-banner__inner">
          <h2 id="wpc-banner-title" class="wpc-title">{{ t('consent.banner.title') }}</h2>
          <p id="wpc-banner-text" class="wpc-text">
            {{ t('consent.banner.text') }}
            <a [href]="policyUrl()" target="_blank" rel="noopener">{{ t('consent.policy_link') }}</a>
          </p>
          <div class="wpc-actions">
            <button cButton color="primary" type="button" (click)="acceptAll('app_banner')">{{ t('consent.accept_all') }}</button>
            <button cButton color="primary" type="button" (click)="necessaryOnly('app_banner')">{{ t('consent.necessary_only') }}</button>
            <button cButton color="primary" type="button" (click)="openPreferences()">{{ t('consent.settings') }}</button>
          </div>
        </div>
      </section>
    }

    @if (prefsOpen()) {
      <div class="wpc-backdrop" (click)="closePreferences()"></div>
      <section #prefs class="wpc-prefs" role="dialog" [attr.lang]="texts.lang()" [attr.dir]="texts.dir()" aria-modal="true" tabindex="-1"
               aria-labelledby="wpc-prefs-title" (keydown)="onPrefsKeydown($event)">
        <div class="wpc-prefs__head">
          <h2 id="wpc-prefs-title" class="wpc-title">{{ t('consent.preferences.title') }}</h2>
          <button type="button" class="btn-close" (click)="closePreferences()"
                  [attr.aria-label]="t('consent.preferences.close')"></button>
        </div>
        <p class="wpc-text">
          {{ t('consent.preferences.intro') }}
          <a [href]="policyUrl()" target="_blank" rel="noopener">{{ t('consent.policy_link') }}</a>
        </p>
        <ul class="wpc-cats">
          @for (cat of offered(); track cat) {
            <li class="wpc-cat">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" [id]="'wpc-cat-' + cat"
                       [attr.aria-describedby]="'wpc-cat-desc-' + cat"
                       [checked]="isChecked(cat)" [disabled]="isLocked(cat)"
                       (change)="toggle(cat, $any($event.target).checked)">
                <label class="form-check-label" [for]="'wpc-cat-' + cat">
                  {{ t('consent.category.' + cat + '.name') }}
                  @if (cat === 'necessary') {
                    <span class="wpc-badge">{{ t('consent.always_active') }}</span>
                  }
                </label>
              </div>
              <p class="wpc-cat__desc" [id]="'wpc-cat-desc-' + cat">
                {{ t('consent.category.' + cat + '.description') }}
                @if (cat === 'marketing' && !draftAnalytics()) {
                  <span class="wpc-hint">{{ t('consent.category.marketing.requires_analytics') }}</span>
                }
              </p>
            </li>
          }
        </ul>
        <div class="wpc-actions">
          <button cButton color="primary" type="button" (click)="necessaryOnly('app_preferences')">{{ t('consent.necessary_only') }}</button>
          <button cButton color="primary" type="button" (click)="saveSelection()">{{ t('consent.save_selection') }}</button>
          <button cButton color="primary" type="button" (click)="acceptAll('app_preferences')">{{ t('consent.accept_all') }}</button>
        </div>
      </section>
    }

    @if (showFloatingLink()) {
      <button type="button" class="wpc-floating-link" [attr.lang]="texts.lang()" [attr.dir]="texts.dir()" (click)="openPreferences()">{{ t('consent.footer_link') }}</button>
    }
  `,
  styles: [`
    :host { position: relative; z-index: 1070; }
    .wpc-banner {
      position: fixed; inset-inline: 0; bottom: 0; z-index: 1070;
      background: var(--cui-body-bg, #fff); color: var(--cui-body-color, #212529);
      border-top: 1px solid var(--cui-border-color, #dee2e6);
      box-shadow: 0 -6px 24px rgba(0, 0, 0, .18);
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
      max-height: 70vh; overflow-y: auto;
    }
    .wpc-banner__inner { max-width: 960px; margin: 0 auto; }
    .wpc-title { font-size: 1.05rem; font-weight: 600; margin: 0 0 6px; }
    .wpc-text { font-size: .9rem; margin: 0 0 12px; color: var(--cui-secondary-color, #6c757d); }
    .wpc-text a, .wpc-cat__desc a { color: var(--cui-link-color, #0d6efd); }
    .wpc-actions { display: grid; grid-template-columns: 1fr; gap: 8px; }
    .wpc-actions .btn { width: 100%; }
    @media (min-width: 576px) {
      .wpc-actions { grid-template-columns: repeat(3, 1fr); }
    }
    .wpc-backdrop { position: fixed; inset: 0; z-index: 1075; background: rgba(0, 0, 0, .45); }
    .wpc-prefs {
      position: fixed; z-index: 1080; inset-inline: 0; bottom: 0;
      max-height: 90vh; overflow-y: auto; outline: none;
      background: var(--cui-body-bg, #fff); color: var(--cui-body-color, #212529);
      border-top-left-radius: 12px; border-top-right-radius: 12px;
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
      box-shadow: 0 -6px 24px rgba(0, 0, 0, .25);
    }
    @media (min-width: 768px) {
      .wpc-prefs {
        inset-inline: auto; left: 50%; bottom: auto; top: 50%; transform: translate(-50%, -50%);
        width: min(640px, calc(100vw - 32px)); border-radius: 12px; padding: 24px;
      }
    }
    .wpc-prefs__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
    .wpc-cats { list-style: none; padding: 0; margin: 0 0 16px; }
    .wpc-cat { padding: 12px 0; border-top: 1px solid var(--cui-border-color, #dee2e6); }
    .wpc-cat .form-check-label { font-weight: 600; }
    .wpc-cat__desc { font-size: .85rem; margin: 4px 0 0; color: var(--cui-secondary-color, #6c757d); }
    .wpc-hint { display: block; margin-top: 4px; font-style: italic; }
    .wpc-badge {
      margin-inline-start: 8px; font-size: .75rem; font-weight: 500; padding: 1px 8px; border-radius: 999px;
      border: 1px solid var(--cui-border-color, #dee2e6); color: var(--cui-secondary-color, #6c757d);
    }
    .wpc-floating-link {
      position: fixed; z-index: 1060; bottom: calc(6px + env(safe-area-inset-bottom, 0px)); inset-inline-start: 6px;
      font-size: .75rem; line-height: 1.2; padding: 3px 8px; border-radius: 999px;
      border: 1px solid var(--cui-border-color, #dee2e6);
      background: var(--cui-body-bg, #fff); color: var(--cui-secondary-color, #6c757d); opacity: .85;
    }
    .wpc-floating-link:hover, .wpc-floating-link:focus-visible { opacity: 1; color: var(--cui-link-color, #0d6efd); }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CookieConsentOverlayComponent implements AfterViewChecked {
  private consent = inject(ConsentService);
  // Instantiated here so `consent_change` is reported even on pages that never track.
  protected readonly tracking = inject(TrackingService);
  /** Consent texts in a reviewed language only (A2/P7) — see ConsentTextsService. */
  protected readonly texts = inject(ConsentTextsService);
  private environment: any = inject(ENVIRONMENT, { optional: true });
  private appConfig = inject(CONSENT_APP_CONFIG, { optional: true });
  private document = inject(DOCUMENT);
  private prefsRef = viewChild<ElementRef<HTMLElement>>('prefs');

  private readonly prefsRequested = this.consent.preferencesOpen;
  /** The center only renders once its texts exist in the consent language. */
  protected readonly prefsOpen = computed(() => this.prefsRequested() && this.texts.ready());
  protected readonly offered = computed(() => { this.consent.config(); return this.consent.offeredCategories; });
  protected readonly showBanner = computed(() =>
    this.texts.ready() && this.consent.needsDecision() && !this.prefsRequested());
  protected readonly showFloatingLink = computed(() =>
    this.texts.ready() && !this.consent.needsDecision() && !this.prefsRequested() && this.consent.footerLinks() === 0);

  protected readonly draftAnalytics = signal(false);
  protected readonly draftMarketing = signal(false);
  private focusPending = false;
  private returnFocus: HTMLElement | null = null;

  constructor() {
    if (this.appConfig?.enabled) this.consent.ready();
    // Initialise the draft from the stored choice each time the center opens.
    effect(() => {
      if (this.prefsOpen()) {
        const c = this.consent.categories();
        this.draftAnalytics.set(c.includes('analytics'));
        this.draftMarketing.set(c.includes('marketing'));
        this.returnFocus = this.document.activeElement as HTMLElement | null;
        this.focusPending = true;
      }
    });
    // A decision taken on the landing page in another tab arrives without reload.
    try {
      this.document.addEventListener('visibilitychange', () => {
        if (this.document.visibilityState === 'visible') this.consent.reread();
      });
    } catch { /* ignore */ }
  }

  ngAfterViewChecked(): void {
    if (this.focusPending && this.prefsRef()) {
      this.focusPending = false;
      this.prefsRef()!.nativeElement.focus();
    }
  }

  protected policyUrl(): string {
    const remote = this.consent.config()?.policy_url;
    if (remote) return remote;
    const base = String(this.environment?.serverUrl ?? '/api').replace(/\/+$/, '');
    const key = this.appConfig?.policyKey || 'privacy';
    // Same language as the banner text around the link.
    return `${base}/media/${encodeURIComponent(this.texts.lang() || 'en')}/policy/${encodeURIComponent(key)}`;
  }

  protected t(key: string): string {
    return this.texts.t(key);
  }

  protected isChecked(cat: ConsentCategory): boolean {
    return cat === 'necessary' || (cat === 'analytics' ? this.draftAnalytics() : this.draftMarketing() && this.draftAnalytics());
  }

  protected isLocked(cat: ConsentCategory): boolean {
    return cat === 'necessary' || (cat === 'marketing' && !this.draftAnalytics());
  }

  protected toggle(cat: ConsentCategory, checked: boolean): void {
    if (cat === 'analytics') {
      this.draftAnalytics.set(checked);
      if (!checked) this.draftMarketing.set(false);
    } else if (cat === 'marketing') {
      this.draftMarketing.set(checked && this.draftAnalytics());
    }
  }

  protected acceptAll(surface: ConsentSurface): void {
    this.consent.grant(this.consent.offeredCategories, surface);
    this.closePreferences();
  }

  protected necessaryOnly(surface: ConsentSurface): void {
    this.consent.grant(['necessary'], surface);
    this.closePreferences();
  }

  protected saveSelection(): void {
    const c: ConsentCategory[] = ['necessary'];
    if (this.draftAnalytics()) c.push('analytics');
    if (this.draftAnalytics() && this.draftMarketing()) c.push('marketing');
    this.consent.grant(c, 'app_preferences');
    this.closePreferences();
  }

  protected openPreferences(): void {
    this.consent.openPreferences();
  }

  protected closePreferences(): void {
    if (!this.prefsRequested()) return;
    this.consent.closePreferences();
    const target = this.returnFocus;
    this.returnFocus = null;
    try { if (target && this.document.contains(target)) target.focus(); } catch { /* ignore */ }
  }

  /** Escape closes; Tab stays inside the dialog. */
  protected onPrefsKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePreferences();
      return;
    }
    if (event.key !== 'Tab') return;
    const root = this.prefsRef()?.nativeElement;
    if (!root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = this.document.activeElement;
    if (event.shiftKey && (active === first || active === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
